import { Controller } from "@hotwired/stimulus"

// CONEK-3608 — Toggles `aria-busy` and a `data-loading` flag on a Turbo Frame
// while it is fetching a new render, so CSS rules can reveal skeleton rows
// during the swap. Stays scoped to the frame the controller is attached to.
//
// Usage:
//   <%= turbo_frame_tag "quotes_list",
//       data: { controller: "ui--frame-busy" } do %>
//     <%# skeleton rows hidden by default, shown when aria-busy=true %>
//     <div data-skeleton-target hidden>…</div>
//     <%# real content %>
//   <% end %>
export default class extends Controller {
  connect() {
    this._onBefore = this._onBefore.bind(this)
    this._onAfter = this._onAfter.bind(this)
    this.element.addEventListener("turbo:before-fetch-request", this._onBefore)
    this.element.addEventListener("turbo:before-fetch-response", this._onAfter)
    this.element.addEventListener("turbo:frame-render", this._onAfter)
    this.element.addEventListener("turbo:frame-load", this._onAfter)
  }

  disconnect() {
    this.element.removeEventListener(
      "turbo:before-fetch-request",
      this._onBefore,
    )
    this.element.removeEventListener(
      "turbo:before-fetch-response",
      this._onAfter,
    )
    this.element.removeEventListener("turbo:frame-render", this._onAfter)
    this.element.removeEventListener("turbo:frame-load", this._onAfter)
  }

  _onBefore(event) {
    // Ignore Turbo 8 hover-prefetch fetches. Prefetch warms the HTTP cache
    // for a FUTURE navigation; it is not loading anything into this frame —
    // but its `turbo:before-fetch-request` bubbles through the frame (the
    // hovered link lives inside it), and marking the frame busy here flashes
    // the skeleton rows on every row hover (the "flickering skeleton" bug on
    // the mobile lists). Turbo stamps prefetch requests with
    // `X-Sec-Purpose: prefetch`; Chrome may send `Sec-Purpose` instead.
    const headers = event?.detail?.fetchOptions?.headers
    const purpose = headers?.["X-Sec-Purpose"] ?? headers?.["Sec-Purpose"]
    if (purpose === "prefetch") return

    this.element.setAttribute("aria-busy", "true")
    this.element.dataset.loading = "true"
  }

  _onAfter() {
    this.element.setAttribute("aria-busy", "false")
    delete this.element.dataset.loading
  }
}
